/**
 * Thuysvers Subscription Cart Modal
 *
 * Module-style API similar to `delivery-core.js`:
 * - export class `TvSubscriptionCartModalController`
 * - export singleton helpers via `TvSubscriptionCartModal`
 *
 * The section provides a static root element:
 * - id="tv-subscription-cart-modal"
 * - [data-tv-subscription-cart-modal]
 * - hidden attribute when closed
 */

import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { getItemProfileKey } from '@theme/cart-profile-cleanup';
import { DeliveryCartAttributes } from '@theme/delivery-cart-attributes';
import { updateAllProductCardsWithQuantities } from '@theme/product-card-updater';

export const TvSubscriptionCartModalSelectors = {
  ROOT_ID: 'tv-subscription-cart-modal',
  ROOT_ATTR: '[data-tv-subscription-cart-modal]',
  SHEET: '.tv-subscription-cart-modal__sheet',
  CLOSE: '[data-tv-subscription-cart-close]',
  NEXT: '.tv-subscription-cart-modal__footer-btn--next',
  CHANGE_MEALS: '.tv-subscription-cart-modal__change-meals-btn',
  SUBSCRIPTION_EDIT: '[data-tv-retake-survey]',
};

const CART_CLEAR_URL =
  typeof Theme !== 'undefined' && Theme?.routes?.cart_url
    ? `${Theme.routes.cart_url.replace(/\/?$/, '')}/clear.js`
    : '/cart/clear.js';

const CART_CHANGE_URL =
  typeof Theme !== 'undefined' && /** @type {any} */ (Theme)?.routes?.cart_change_url
    ? /** @type {any} */ (Theme).routes.cart_change_url
    : '/cart/change.js';
const CART_UPDATE_URL =
  typeof Theme !== 'undefined' && /** @type {any} */ (Theme)?.routes?.cart_update_url
    ? /** @type {any} */ (Theme).routes.cart_update_url
    : '/cart/update.js';

/**
 * Remove document-level inline cart scroll lock (html/body overflow). Safe to call anytime.
 */
function clearInlineStepDocumentLocks() {
  try {
    document.documentElement.classList.remove('tv-subscription-cart-inline-step');
    document.documentElement.style.removeProperty('--tv-cart-inline-top');
    document.documentElement.style.removeProperty('--tv-cart-inline-bottom');
  } catch {
    /* ignore */
  }
}

/**
 * After section morph/render the modal DOM is replaced; openInline() state on documentElement survives.
 * Clears the lock and notifies the footer (same as a real inline close) only if the lock was active.
 */
function clearInlineStepAfterSectionMorph() {
  if (!document.documentElement.classList.contains('tv-subscription-cart-inline-step')) return;
  clearInlineStepDocumentLocks();
  try {
    window.dispatchEvent(new CustomEvent('tv-subscription-cart-inline-closed'));
  } catch {
    /* ignore */
  }
}

/**
 * Classify a /cart.js line item as meal / soup / dessert.
 * Mirrors the kind logic in snippets/tv-subscription-cart-item-kind.liquid and
 * the guest JS — uses product_type directly (metafields are not in the cart API).
 * @param {{ product_type?: string }} item
 * @returns {'meal'|'soup'|'dessert'}
 */
function getCartItemKind(item) {
  const t = String(item?.product_type || '').toLowerCase().trim();
  if (t === 'soep' || t === 'soup') return 'soup';
  if (t === 'nagerechten' || t === 'toetjes') return 'dessert';
  return 'meal';
}

/**
 * @param {any} item — line from GET /cart.js
 * @returns {boolean}
 */
function cartLineHasSellingPlan(item) {
  return item?.selling_plan_allocation != null;
}

/**
 * True if the line was stamped at add time on the subscription collection (see product-form.js).
 * @param {any} item
 * @returns {boolean}
 */
function cartLineHasSubscriptionLineUid(item) {
  const p = item?.properties;
  const key = '_subscription_line_uid';
  if (!p) return false;
  if (typeof p === 'object' && !Array.isArray(p) && p[key] != null) {
    return String(p[key]).trim() !== '';
  }
  if (Array.isArray(p)) {
    const e = p.find((x) => x && (x.name === key || x.key === key));
    return e ? String(e.value || e.val || '').trim() !== '' : false;
  }
  return false;
}

/**
 * Read subscription thresholds needed for reconciliation.
 * - Logged-in users: reads from the JSON script tag injected by Liquid
 *   (#tv-subscription-modal-thresholds).
 * - Guest users: falls back to sessionStorage key 'recommendations' (same source
 *   used by thuysvers-subscription-cart-modal-guest.js).
 * @returns {{ profiles: Array<{key: string, mealsPerWeek: number}>, soupExpected: number, dessertExpected: number } | null}
 */
function getSubscriptionThresholds() {
  // 1. Logged-in: DOM script tag embedded by Liquid
  try {
    const el = document.getElementById('tv-subscription-modal-thresholds');
    if (el?.textContent) {
      const d = JSON.parse(el.textContent.trim());
      if (d && Array.isArray(d.profiles) && d.profiles.length > 0) {
        return {
          profiles: d.profiles
            .map((/** @type {any} */ p) => ({ key: String(p.key || '').trim(), mealsPerWeek: Number(p.mealsPerWeek) || 0 }))
            .filter((/** @type {any} */ p) => p.key),
          soupExpected: Number(d.soupExpected) || 0,
          dessertExpected: Number(d.dessertExpected) || 0,
        };
      }
    }
  } catch { /* fall through */ }

  // 2. Guest: sessionStorage 'recommendations'
  try {
    const raw = sessionStorage.getItem('recommendations');
    if (raw) {
      const payload = JSON.parse(raw);
      const profiles = Array.isArray(payload.profiles)
        ? payload.profiles
            .map((/** @type {any} */ p) => ({ key: String(p.profileKey || '').trim(), mealsPerWeek: Number(p.mealsPerWeek) || 0 }))
            .filter((/** @type {any} */ p) => p.key)
        : [];
      let soupExpected = 0;
      let dessertExpected = 0;
      (Array.isArray(payload.addOns) ? payload.addOns : []).forEach((/** @type {any} */ a) => {
        const type = String(a.addonType || '').toLowerCase();
        if (type === 'soup') soupExpected += Number(a.quantity) || 0;
        else if (type === 'dessert') dessertExpected += Number(a.quantity) || 0;
      });
      if (profiles.length > 0 || soupExpected > 0 || dessertExpected > 0) {
        return { profiles, soupExpected, dessertExpected };
      }
    }
  } catch { /* ignore */ }

  return null;
}

export class TvSubscriptionCartModalController {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    /** @type {HTMLElement} */
    this.root = root;
    /** @type {Element | null} */
    this.sheet = root.querySelector(TvSubscriptionCartModalSelectors.SHEET);
    /** @type {HTMLElement | null} */
    this.nextButton = root.querySelector(TvSubscriptionCartModalSelectors.NEXT);
    /** @type {boolean} */
    this.bound = false;
    /** @type {boolean} */
    this.advancing = false;
  }

  bind() {
    if (this.bound) return;
    this.bound = true;

    const closeButtons = this.root.querySelectorAll(TvSubscriptionCartModalSelectors.CLOSE);
    closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));

    if (this.nextButton instanceof HTMLElement) {
      this.nextButton.addEventListener('click', this.#handleNextClick);
    }

    const changeButtons = this.root.querySelectorAll(TvSubscriptionCartModalSelectors.CHANGE_MEALS);
    changeButtons.forEach((btn) => {
      if (btn instanceof HTMLElement) {
        btn.addEventListener('click', this.#handleChangeMealsClick);
      }
    });

    this.root.addEventListener('click', this.#handleSubscriptionEditClick);
  }

  isOpen() {
    return !this.root.hidden;
  }

  isInlineStep() {
    return this.root.classList.contains('is-inline-step');
  }

  open() {
    this.root.hidden = false;
    requestAnimationFrame(() => {
      this.root.classList.add('is-open');
    });
    document.documentElement.classList.add('tv-subscription-cart-modal-open');
   // Let guest renderer (and any other listeners) refresh content on open.
   try {
      window.dispatchEvent(new CustomEvent('tv-subscription-cart-modal-opened'));
    } catch {
      console.error('[THUYSVERS-SUBSCRIPTION-CART-MODAL] Error in open');
    }

    // Fix any _profile_key overflow mismatches in the background.
    this.reconcileProfileKeys().catch(() => {});

    return true;
  }

  /**
   * Open the cart as an inline step (replaces the product grid area, no side drawer).
   * The subscription footer remains visible below; the cart fills the space between
   * the page header and the footer.
   */
  openInline() {
    const mainSection = document.querySelector('[id^="shopify-section-"][id$="__main"]');
    const headerEl =
      document.querySelector('sticky-header') ||
      document.querySelector('.header-group') ||
      document.querySelector('#shopify-section-header > *') ||
      document.querySelector('.section-header');
    const footerEl = document.querySelector('custom-collection-subscription-footer');

    const mainTop = mainSection instanceof HTMLElement
      ? Math.max(0, Math.round(mainSection.getBoundingClientRect().top))
      : 0;
    const headerH = mainTop > 0
      ? mainTop
      : headerEl ? Math.round(headerEl.getBoundingClientRect().height) : 0;
    const footerH = footerEl ? Math.round(footerEl.getBoundingClientRect().height) : 0;

    // Apply CSS variables and classes BEFORE unhiding so the element is correctly
    // positioned and styled on the very first paint — prevents any flicker.
    document.documentElement.style.setProperty('--tv-cart-inline-top', `${headerH}px`);
    document.documentElement.style.setProperty('--tv-cart-inline-bottom', `${footerH}px`);
    this.root.classList.add('is-inline-step', 'is-open');
    document.documentElement.classList.add('tv-subscription-cart-inline-step');

    // Now make the element visible — styles are already in place.
    this.root.hidden = false;

    // Sync the footer stepper to reflect the overview/cart step:
    // mark every visible non-shipping step as complete, activate the shipping
    // circle, and push the progress bar fill to 100%.
    const stepperRoot = document.querySelector('[data-tv-subscription-footer-stepper]');
    if (stepperRoot instanceof HTMLElement) {
      const fillEl = stepperRoot.querySelector('[data-tv-stepper-fill]');
      const stepEls = Array.from(
        stepperRoot.querySelectorAll('.tv-subscription-footer-stepper__step')
      ).filter((el) => el instanceof HTMLElement && !/** @type {HTMLElement} */ (el).hidden);

      stepEls.forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        const t = String(el.getAttribute('data-tv-stepper') || '');
        if (t === 'shipping') {
          el.classList.add('is-active');
          el.classList.remove('is-complete');
        } else {
          el.classList.remove('is-active');
          el.classList.add('is-complete');
        }
      });

      if (fillEl instanceof HTMLElement) {
        fillEl.style.width = '100%';
      }
    }

    try {
      window.dispatchEvent(new CustomEvent('tv-subscription-cart-modal-opened'));
      window.dispatchEvent(new CustomEvent('tv-subscription-cart-inline-opened'));
    } catch {
      console.error('[THUYSVERS-SUBSCRIPTION-CART-MODAL] Error in openInline');
    }

    // Fix any _profile_key overflow mismatches in the background.
    this.reconcileProfileKeys().catch(() => {});

    return true;
  }

  /**
   * Close the inline step cart and restore the product grid.
   */
  closeInline() {
    this.root.classList.remove('is-open', 'is-inline-step');
    clearInlineStepDocumentLocks();
    this.root.hidden = true;

    // Remove inline stepper state — syncStepperFromState (triggered by the
    // tv-subscription-cart-inline-closed event below) will restore the correct step.
    const shippingStep = document.querySelector('[data-tv-stepper="shipping"]');
    if (shippingStep instanceof HTMLElement) {
      shippingStep.classList.remove('is-active', 'is-complete');
    }
    const fillEl = document.querySelector('[data-tv-stepper-fill]');
    if (fillEl instanceof HTMLElement) {
      fillEl.style.width = '';
    }

    try {
      window.dispatchEvent(new CustomEvent('tv-subscription-cart-inline-closed'));
    } catch {
      console.error('[THUYSVERS-SUBSCRIPTION-CART-MODAL] Error in closeInline');
    }

    return true;
  }

  /**
   * Check cart line items against subscription thresholds and fix any _profile_key
   * mismatches. Overflow items (beyond the subscription cap) are removed from the cart
   * (full line delete, or quantity reduced when only part of the line overflows).
   *
   * Removes any line that has neither `_subscription_line_uid` (subscription add stamp)
   * nor a selling plan — those do not belong in this flow.
   *
   * Mirrors the display logic in sections/thuysvers-subscription-cart-modal.liquid:
   * items are processed oldest-first; the first N fill subscription slots and any
   * excess (last-added) is removed.
   *
   * For line items where ALL units overflow → remove the line (quantity 0).
   * For line items where SOME units overflow → reduce the line to the units that fit.
   *
   * After any changes the modal section is morphed with the updated server HTML so
   * both the logged-in Liquid view and the guest JS renderer see correct data.
   *
   * @returns {Promise<boolean>} true if any cart changes were made
   */
  async reconcileProfileKeys() {
    try {
      const sectionId = this.root.getAttribute('data-section-id') || '';
      const sectionsUrl =
        typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';
      const headers = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

      let cartData =
        /** @type {(Record<string, unknown> & { items?: unknown[] }) | null} */ (
          await DeliveryCartAttributes.getCartData()
        );
      if (!cartData) return false;
      let items = /** @type {any[]} */ (Array.isArray(cartData.items) ? cartData.items : []);
      if (!items.length) return true;

      /** @type {boolean} */
      let morphApplied = false;
      /** @type {boolean} */
      let didRemoveUnstampedLines = false;

      const removalKeys = items
        .filter((item) => !cartLineHasSubscriptionLineUid(item) && !cartLineHasSellingPlan(item))
        .map((item) => item.key);

      if (removalKeys.length > 0) {
        const updates = /** @type {Record<string, number>} */ ({});
        removalKeys.forEach((k) => {
          updates[k] = 0;
        });
        const body = /** @type {Record<string, unknown>} */ ({ updates });
        if (sectionId) {
          body['sections'] = sectionId;
          body['sections_url'] = sectionsUrl;
        }
        const res = await fetch(CART_UPDATE_URL, { method: 'POST', headers, body: JSON.stringify(body) });
        if (res.ok) {
          didRemoveUnstampedLines = true;
          const data = await res.json().catch(() => null);
          if (sectionId && data?.sections?.[sectionId]) {
            try {
              morphSection(sectionId, data.sections[sectionId]);
              morphApplied = true;
              clearInlineStepAfterSectionMorph();
            } catch {
              /* ignore */
            }
          }
          const refreshed = await DeliveryCartAttributes.getCartData();
          if (refreshed) {
            cartData = /** @type {Record<string, unknown> & { items?: unknown[] }} */ (refreshed);
            items = /** @type {any[]} */ (Array.isArray(cartData.items) ? cartData.items : []);
          }
        }
      }

      const notifyCartUpdate = async () => {
        try {
          document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
        } catch {
          /* ignore */
        }
        // Refresh every product card overlay — full sync used to skip products no longer in cart.
        try {
          await updateAllProductCardsWithQuantities(true);
        } catch {
          /* ignore */
        }
      };

      const ensureModalMorph = async () => {
        if (!sectionId || morphApplied) return;
        try {
          await sectionRenderer.renderSection(sectionId, { cache: false });
          morphApplied = true;
          clearInlineStepAfterSectionMorph();
        } catch {
          /* ignore */
        }
      };

      if (!items.length) {
        await notifyCartUpdate();
        await ensureModalMorph();
        return true;
      }

      const thresholds = getSubscriptionThresholds();
      if (!thresholds) {
        if (didRemoveUnstampedLines) {
          await notifyCartUpdate();
          await ensureModalMorph();
          return true;
        }
        return false;
      }

      // Remaining subscription slots per category
      const profileSlots = new Map(thresholds.profiles.map((p) => [p.key, p.mealsPerWeek]));
      let soupLeft = thresholds.soupExpected;
      let dessertLeft = thresholds.dessertExpected;

      // Per-line decision: how many units stay vs overflow (overflow units are removed)
      /** @type {Array<{keepCount: number, changeCount: number}>} */
      const decisions = items.map(() => ({ keepCount: 0, changeCount: 0 }));

      // Process oldest-first (items[last] = oldest in Shopify's newest-first array)
      for (let i = items.length - 1; i >= 0; i--) {
        const item = items[i];
        const d = decisions[i];
        if (!d) continue;
        const pk = getItemProfileKey(item);
        const kind = getCartItemKind(item);
        const qty = Number(item.quantity) || 0;

        if (!pk || pk === 'oneoff') {
          d.keepCount += qty; // already correct — no change needed
          continue;
        }

        for (let q = 0; q < qty; q++) {
          let fits = false;
          if (kind === 'meal' && profileSlots.has(pk)) {
            // Known profile key: consume a slot if available, otherwise overflow → remove
            const slots = profileSlots.get(pk) ?? 0;
            if (slots > 0) { profileSlots.set(pk, slots - 1); fits = true; }
          } else if (kind === 'soup' && pk === 'soup') {
            if (soupLeft > 0) { soupLeft--; fits = true; }
          } else if (kind === 'dessert' && pk === 'dessert') {
            if (dessertLeft > 0) { dessertLeft--; fits = true; }
          } else {
            // Genuine kind/key mismatch (e.g. meal tagged 'soup', unknown pk) — leave as-is
            fits = true;
          }
          if (fits) d.keepCount++;
          else d.changeCount++;
        }
      }

      const ops = decisions
        .map((d, i) => ({ ...d, item: items[i] }))
        .filter((d) => d.changeCount > 0);

      if (!ops.length) {
        if (didRemoveUnstampedLines) {
          await notifyCartUpdate();
          await ensureModalMorph();
          return true;
        }
        return false;
      }

      /** @type {boolean} */
      let morphAppliedThreshold = false;

      for (let oi = 0; oi < ops.length; oi++) {
        const op = ops[oi];
        if (!op) continue;
        const { item, keepCount } = op;
        const isLast = oi === ops.length - 1;

        // Remove overflow only: delete whole line, or lower quantity (no re-key to oneoff / no selling_plan edits).
        const body = /** @type {Record<string, unknown>} */ ({
          id: item.key,
          quantity: keepCount === 0 ? 0 : keepCount,
        });
        if (isLast && sectionId) {
          body['sections'] = sectionId;
          body['sections_url'] = sectionsUrl;
        }
        const res = await fetch(CART_CHANGE_URL, { method: 'POST', headers, body: JSON.stringify(body) });
        if (isLast && sectionId) {
          const data = await res.json().catch(() => null);
          if (data?.sections?.[sectionId]) {
            try {
              morphSection(sectionId, data.sections[sectionId]);
              morphAppliedThreshold = true;
              clearInlineStepAfterSectionMorph();
            } catch {
              /* ignore */
            }
          }
        } else if (res.ok) {
          // Non-last ops: still need fresh cart for subsequent id keys if Shopify rotates keys
          void res.json().catch(() => null);
        }
      }

      if (sectionId && !morphApplied && !morphAppliedThreshold) {
        try {
          await sectionRenderer.renderSection(sectionId, { cache: false });
          clearInlineStepAfterSectionMorph();
        } catch {
          /* ignore */
        }
      }

      // Notify guest renderer and other listeners that the cart changed
      await notifyCartUpdate();

      return true;
    } catch (err) {
      console.error('[THUYSVERS-SUBSCRIPTION-CART-MODAL] reconcileProfileKeys error:', err);
      return false;
    }
  }

  close() {
    // When displayed as an inline step, use the inline close path.
    if (this.isInlineStep()) {
      return this.closeInline();
    }

    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('tv-subscription-cart-modal-open');

    const finish = () => {
      this.root.hidden = true;
      this.root.removeEventListener('transitionend', onEnd);
    };

    /** @param {TransitionEvent} e */
    const onEnd = (e) => {
      if (this.sheet && e.target !== this.sheet) return;
      finish();
    };

    this.root.addEventListener('transitionend', onEnd);
    window.setTimeout(finish, 300);
    return true;
  }

  toggle() {
    return this.isOpen() ? this.close() : this.open();
  }

  /** @param {Event} event */
  #handleChangeMealsClick = (event) => {
    try {
      const target = /** @type {HTMLElement | null} */ (event.currentTarget instanceof HTMLElement ? event.currentTarget : null);
      if (!target) return;

      const handle = String(target.dataset?.tvStepHandle || '').trim();
      if (!handle) return;

      // Dispatch first so the footer can read the current step (fromHandle) before updating.
      // The footer will persist the handle and sync filters; updating sessionStorage here
      // would make fromHandle === handle and skip filter sync.
      window.dispatchEvent(
        new CustomEvent('tv-subscription-step-changed', {
          detail: { handle },
        }),
      );

      this.close();
    } catch (error) {
      console.error('[THUYSVERS-SUBSCRIPTION-CART-MODAL] Error in handleChangeMealsClick', error);
    }
  };

  /**
   * When "retake survey" / "Aanpassen" (subscription edit) is clicked, clear cart then navigate.
   * @param {Event} event
   */
  #handleSubscriptionEditClick = async (event) => {
    const target = event.target instanceof Element ? event.target.closest(TvSubscriptionCartModalSelectors.SUBSCRIPTION_EDIT) : null;
    if (!target || !(target instanceof HTMLAnchorElement) || !target.href) return;
    event.preventDefault();
    event.stopPropagation();
    const url = target.href;
    try {
      const res = await fetch(CART_CLEAR_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      if (!res.ok) {
        console.warn('[TvSubscriptionCartModal] Failed to clear cart before retake:', res.status);
      }
    } catch (err) {
      console.warn('[TvSubscriptionCartModal] Error clearing cart before retake:', err);
    }
    window.location.href = url;
  };

  /** @param {Event} event */
  #handleNextClick = async (event) => {
    try {
      event.preventDefault();
      // Defensive: prevent any other click handlers from double-advancing.
      try {
        event.stopPropagation();
        // @ts-ignore
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      } catch {
        // ignore
      }

      if (this.advancing) return;
      this.advancing = true;

      const footerComponent = document.querySelector('custom-collection-subscription-footer');
      if (footerComponent && 'advanceToNextStep' in footerComponent && typeof /** @type {any} */ (footerComponent).advanceToNextStep === 'function') {
        await /** @type {any} */ (footerComponent).advanceToNextStep({ source: 'cart-modal' });
      }
    } catch (error) {
      // ignore
      console.error('[THUYSVERS-SUBSCRIPTION-CART-MODAL] Error in handleNextClick', error);
    } finally {
      this.advancing = false;
    }
  };
}

/** @type {TvSubscriptionCartModalController | null} */
let _instance = null;

/**
 * @returns {TvSubscriptionCartModalController | null}
 */
export function getTvSubscriptionCartModal() {
  const root = document.getElementById(TvSubscriptionCartModalSelectors.ROOT_ID) || document.querySelector(TvSubscriptionCartModalSelectors.ROOT_ATTR);
  if (!(root instanceof HTMLElement)) return null;

  if (!_instance || _instance.root !== root) {
    _instance = new TvSubscriptionCartModalController(root);
  }
  _instance.bind();
  return _instance;
}

export const TvSubscriptionCartModal = {
  get: getTvSubscriptionCartModal,
  open: () => getTvSubscriptionCartModal()?.open() ?? false,
  close: () => getTvSubscriptionCartModal()?.close() ?? false,
  toggle: () => getTvSubscriptionCartModal()?.toggle() ?? false,
  isOpen: () => getTvSubscriptionCartModal()?.isOpen() ?? false,
  openInline: () => getTvSubscriptionCartModal()?.openInline() ?? false,
  closeInline: () => getTvSubscriptionCartModal()?.closeInline() ?? false,
  isInlineStep: () => getTvSubscriptionCartModal()?.isInlineStep() ?? false,
  reconcileProfileKeys: () => getTvSubscriptionCartModal()?.reconcileProfileKeys() ?? Promise.resolve(false),
};

// Initialize bindings once DOM is ready (ensures close buttons work).
const init = () => {
  getTvSubscriptionCartModal();

  // Also expose on window for non-module consumers.
  if (typeof window !== 'undefined') {
    /** @type {any} */ (window).TvSubscriptionCartModal = TvSubscriptionCartModal;
    /** @type {any} */ (window).getTvSubscriptionCartModal = getTvSubscriptionCartModal;
  }
};

try {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
} catch {
  init();
}

